import { get } from './client.ts'
import type { GeoJSONFeatureCollection } from '../types/topology.ts'

export function getSubstationsGeoJSON(): Promise<GeoJSONFeatureCollection> {
  return get<GeoJSONFeatureCollection>('/map/substations')
}

export function getFeedersGeoJSON(): Promise<GeoJSONFeatureCollection> {
  return get<GeoJSONFeatureCollection>('/map/feeders')
}

export function getTransformersGeoJSON(params?: {
  feeder_id?: number
  status?: string
}): Promise<GeoJSONFeatureCollection> {
  const qs = new URLSearchParams()
  if (params?.feeder_id) qs.set('feeder_id', String(params.feeder_id))
  if (params?.status) qs.set('verification_status', params.status)
  const q = qs.toString()
  return get<GeoJSONFeatureCollection>(`/map/transformers${q ? `?${q}` : ''}`)
}

export interface BBox {
  minLon: number
  minLat: number
  maxLon: number
  maxLat: number
}

export function getMetersGeoJSON(params?: {
  transformer_id?: number
  status?: string
  bbox?: BBox
}): Promise<GeoJSONFeatureCollection> {
  const qs = new URLSearchParams()
  if (params?.transformer_id) qs.set('transformer_id', String(params.transformer_id))
  if (params?.status) qs.set('verification_status', params.status)
  if (params?.bbox) {
    qs.set('min_lon', String(params.bbox.minLon))
    qs.set('min_lat', String(params.bbox.minLat))
    qs.set('max_lon', String(params.bbox.maxLon))
    qs.set('max_lat', String(params.bbox.maxLat))
  }
  const q = qs.toString()
  return get<GeoJSONFeatureCollection>(`/map/meters${q ? `?${q}` : ''}`)
}

export function getConductorsGeoJSON(params?: {
  feeder_id?: number
  segment_type?: string
}): Promise<GeoJSONFeatureCollection> {
  const qs = new URLSearchParams()
  if (params?.feeder_id) qs.set('feeder_id', String(params.feeder_id))
  if (params?.segment_type) qs.set('segment_type', params.segment_type)
  const q = qs.toString()
  return get<GeoJSONFeatureCollection>(`/map/conductors${q ? `?${q}` : ''}`)
}

export function getSecondaryGeoJSON(params?: {
  bbox?: BBox
  circuit?: string
  segment_type?: string
  feeder_id?: number
  /** Opt-in: enrich each feature with derived connectivity props
   *  (root_transformer_id/root_method/root_confidence/tier/transformer_reachable)
   *  for the color-by-confidence mode. Null props where a feeder has no
   *  published run (graceful degrade). */
  include_connectivity?: boolean
}): Promise<GeoJSONFeatureCollection> {
  const qs = new URLSearchParams()
  if (params?.bbox) {
    qs.set('min_lon', String(params.bbox.minLon))
    qs.set('min_lat', String(params.bbox.minLat))
    qs.set('max_lon', String(params.bbox.maxLon))
    qs.set('max_lat', String(params.bbox.maxLat))
  }
  if (params?.circuit) qs.set('circuit', params.circuit)
  if (params?.segment_type) qs.set('segment_type', params.segment_type)
  if (params?.feeder_id) qs.set('feeder_id', String(params.feeder_id))
  if (params?.include_connectivity) qs.set('include_connectivity', 'true')
  const q = qs.toString()
  return get<GeoJSONFeatureCollection>(`/map/secondary${q ? `?${q}` : ''}`)
}

export function getDevicesGeoJSON(params?: {
  feeder_id?: number
  device_type?: string
}): Promise<GeoJSONFeatureCollection> {
  const qs = new URLSearchParams()
  if (params?.feeder_id) qs.set('feeder_id', String(params.feeder_id))
  if (params?.device_type) qs.set('device_type', params.device_type)
  const q = qs.toString()
  return get<GeoJSONFeatureCollection>(`/map/devices${q ? `?${q}` : ''}`)
}
